import { supabase } from '@/integrations/supabase/client';
import { ensureServiceWorkerRegistration, urlBase64ToUint8Array, vapidPublicKey } from './common';

export async function thisLocationDeviceSubscribed(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
  if (!registration) return false;
  const sub = await registration.pushManager.getSubscription();
  return sub != null;
}

export async function subscribeThisLocationDevice(groupId: string): Promise<void> {
  const key = vapidPublicKey();

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('push: this browser cannot subscribe to Web Push');
  }
  if (typeof Notification === 'undefined') {
    throw new Error('push: Notification API is not available');
  }
  if (Notification.permission === 'denied') {
    throw new Error('push: notification permission denied');
  }
  if (Notification.permission !== 'granted') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') {
      throw new Error('push: notification permission denied');
    }
  }

  const registration = await ensureServiceWorkerRegistration();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
  });
  const json = subscription.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) {
    throw new Error('push: subscription is missing endpoint or keys');
  }

  const { error } = await supabase.rpc('location_push_subscribe' as never, {
    p_group_id: groupId,
    p_endpoint: endpoint,
    p_p256dh: p256dh,
    p_auth: auth,
    p_user_agent: navigator.userAgent,
  } as never);
  if (error) {
    // Browser subscribed but the server rejected it — drop the orphan
    // browser subscription rather than leaving a live endpoint the server
    // doesn't know about (Nest #155 lesson).
    await subscription.unsubscribe();
    throw error;
  }
}

export async function thisLocationDeviceBinding(): Promise<{ groupId: string; groupName: string } | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
  if (!registration) return null;
  const sub = await registration.pushManager.getSubscription();
  if (!sub) return null;

  const endpoint = sub.endpoint;
  if (!endpoint) return null;

  const { data, error } = await supabase.rpc('location_push_status' as never, {
    p_endpoint: endpoint,
  } as never);
  if (error) throw error;

  const row = Array.isArray(data) ? (data[0] as { group_id: string; group_name: string } | undefined) : undefined;
  if (!row) return null;
  return { groupId: row.group_id, groupName: row.group_name };
}

export async function rebindThisLocationDevice(groupId: string): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
  if (!registration) return;
  const sub = await registration.pushManager.getSubscription();
  if (!sub) return;

  const json = sub.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) return;

  const { error } = await supabase.rpc('location_push_subscribe' as never, {
    p_group_id: groupId,
    p_endpoint: endpoint,
    p_p256dh: p256dh,
    p_auth: auth,
    p_user_agent: navigator.userAgent,
  } as never);
  if (error) throw error;
}

export async function unsubscribeThisLocationDevice(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('push: this browser cannot unsubscribe from Web Push');
  }
  const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
  if (!registration) {
    throw new Error('push: no service worker registration to unsubscribe');
  }
  const sub = await registration.pushManager.getSubscription();
  if (!sub) {
    throw new Error('push: this device is not subscribed');
  }

  // Browser first, DB second: if the RPC fails afterwards the leftover row is
  // pruned by dispatch on the first 404/410. The reverse order leaves a live
  // endpoint the server no longer knows about.
  const endpoint = sub.endpoint;
  const ok = await sub.unsubscribe();
  if (!ok) throw new Error('push: browser refused to unsubscribe');

  const { error } = await supabase.rpc('location_push_unsubscribe' as never, {
    p_endpoint: endpoint,
  } as never);
  if (error) throw error;
}
